import React from 'react';

import { spacings } from '@lumapps/classnames';
import { Chip, ChipProps } from '@lumapps/lumx/react';

import { UseListFilter, useListFilter, UseListFilterOptions } from '../../hooks/useListFilter';
import { defaultGetId } from '../../utils/defaultGetId';
import { Filter, type FilterProps } from '../Filter';

export interface ChipFilterProps<T> extends FilterProps {
    /** list of options to display as chips */
    options: T[];
    /** determines how to render the react node for the chip */
    renderChip: (entity: T) => React.ReactNode;
    /** custom props for the rendered chip */
    chipProps?: (entity: T) => Partial<ChipProps>;
    /** Callback to get the id for each filter.  */
    getId?: (entity: T) => string;
    /** default values */
    defaults?: Partial<UseListFilterOptions<T>>;
    /**  USED INTERNALLY, DO NOT USE, INJECTED BY PARENT `Filters` */
    onSelected?: UseListFilter<T>['onSelected'];
    /**  USED INTERNALLY, DO NOT USE, INJECTED BY PARENT `Filters` */
    isInSelection?: UseListFilter<T>['isInSelection'];
}

/**
 * Component that renders a chip filter, used in the `@lumapps/lumx-filter-and-sort` pattern.
 * Commonly used for showing statuses, such as `Draft`, `Active`, `Inactive`, `Archived`
 *
 * @family Filters
 * @param ChipFilterProps
 * @returns ChipFilter
 */
export const ChipFilter = <T,>({
    options,
    renderChip,
    onSelected,
    isInSelection,
    chipProps = () => ({}),
    getId = defaultGetId,
    ...props
}: ChipFilterProps<T>) => {
    return (
        <Filter {...props} isFieldset titleAs="heading">
            {options.map((option, index) => (
                <Chip
                    key={getId ? getId(option) : index}
                    className={spacings([
                        { type: 'margin', direction: 'right' },
                        { type: 'margin', direction: 'bottom' },
                    ])}
                    onClick={() => onSelected?.(option)}
                    isSelected={isInSelection?.(option)}
                    {...chipProps(option)}
                >
                    {renderChip(option)}
                </Chip>
            ))}
        </Filter>
    );
};

Object.assign(ChipFilter, { hook: useListFilter });
